/**
 * Utility functions for converting between imperial and metric measurements
 */

// Conversion factors
const CONVERSION_FACTORS = {
  // Volume conversions
  'oz_to_ml': 29.5735,
  'ml_to_oz': 0.033814,
  'tsp_to_ml': 4.92892,
  'ml_to_tsp': 0.202884,
  'tbsp_to_ml': 14.7868,
  'ml_to_tbsp': 0.067628,
  'cup_to_ml': 236.588,
  'ml_to_cup': 0.00422675,
  
  // Other conversions maintain the same value
  'dash_to_dash': 1,
  'drop_to_drop': 1,
  'pinch_to_pinch': 1,
  'barspoon_to_barspoon': 1
};

// Units by system
const IMPERIAL_UNITS = ['oz', 'tsp', 'tbsp', 'cup', 'dash', 'drop', 'pinch', 'barspoon'];
const METRIC_UNITS = ['ml', 'cl', 'dash', 'drop', 'pinch', 'barspoon'];

/**
 * Converts a measurement to the target system
 * @param {Object} measurement - The measurement object with value and unit
 * @param {String} targetSystem - The target measurement system ('imperial' or 'metric')
 * @returns {Object} - Converted measurement with value and unit
 */
export const convertMeasurement = (measurement, targetSystem) => {
  if (!measurement || !measurement.value || !measurement.unit) {
    return measurement;
  }
  
  const value = parseFloat(measurement.value);
  const unit = measurement.unit.toLowerCase();
  
  // If the unit is already in the target system, return as is
  if (targetSystem === 'imperial' && IMPERIAL_UNITS.includes(unit)) {
    return measurement;
  }
  if (targetSystem === 'metric' && METRIC_UNITS.includes(unit)) {
    return measurement;
  }
  
  let convertedValue, convertedUnit;
  
  // Convert to target system
  if (targetSystem === 'metric') {
    switch (unit) {
      case 'oz':
        convertedValue = value * CONVERSION_FACTORS.oz_to_ml;
        convertedUnit = 'ml';
        break;
      case 'tsp':
        convertedValue = value * CONVERSION_FACTORS.tsp_to_ml;
        convertedUnit = 'ml';
        break;
      case 'tbsp':
        convertedValue = value * CONVERSION_FACTORS.tbsp_to_ml;
        convertedUnit = 'ml';
        break;
      case 'cup':
        convertedValue = value * CONVERSION_FACTORS.cup_to_ml;
        convertedUnit = 'ml';
        break;
      default:
        convertedValue = value;
        convertedUnit = unit;
    }
  } else { // imperial
    switch (unit) {
      case 'ml':
        if (value >= 30) { // Larger amounts convert to oz
          convertedValue = value * CONVERSION_FACTORS.ml_to_oz;
          convertedUnit = 'oz';
        } else if (value >= 15) { // Medium amounts convert to tbsp
          convertedValue = value * CONVERSION_FACTORS.ml_to_tbsp;
          convertedUnit = 'tbsp';
        } else { // Small amounts convert to tsp
          convertedValue = value * CONVERSION_FACTORS.ml_to_tsp;
          convertedUnit = 'tsp';
        }
        break;
      case 'cl':
        convertedValue = value * 10 * CONVERSION_FACTORS.ml_to_oz; // cl to ml to oz
        convertedUnit = 'oz';
        break;
      default:
        convertedValue = value;
        convertedUnit = unit;
    }
  }
  
  // Round to 2 decimal places for readability
  convertedValue = Math.round(convertedValue * 100) / 100;
  
  return {
    value: convertedValue,
    unit: convertedUnit
  };
};

/**
 * Formats a measurement for display
 * @param {Object} measurement - The measurement object with value and unit
 * @returns {String} - Formatted measurement string
 */
export const formatMeasurement = (measurement) => {
  if (!measurement || !measurement.value || !measurement.unit) {
    return '';
  }
  
  return `${measurement.value} ${measurement.unit}`.trim();
};

/**
 * Converts and formats a measurement for display
 * @param {Object} measurement - The measurement object with value and unit
 * @param {String} targetSystem - The target measurement system ('imperial' or 'metric')
 * @param {Number} scaleFactor - Optional scale factor for the measurement (for servings)
 * @returns {String} - Converted and formatted measurement string
 */
export const getDisplayMeasurement = (measurement, targetSystem, scaleFactor = 1) => {
  if (!measurement) return '';
  
  // Apply scaling
  const scaledMeasurement = {
    value: parseFloat(measurement.value) * scaleFactor,
    unit: measurement.unit
  };
  
  // Convert to target system
  const convertedMeasurement = convertMeasurement(scaledMeasurement, targetSystem);
  
  // Format for display
  return formatMeasurement(convertedMeasurement);
}; 